import { focusHaloFlush, panelMotion } from "@pax/ui";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

/** How much of the rail the fade at each end covers. */
const RAIL_FADE = "3.5rem";

/**
 * A row that runs off its edges, and says so.
 *
 * The end it can still be scrolled towards is faded — by a mask on the row
 * itself rather than a gradient laid over it, because the page underneath is a
 * colour sampled from the cover and no fixed gradient could match it. A step
 * control sits in each live fade, for pointers that would rather press than
 * swipe; it appears on hover and is the only thing there that takes a press,
 * so the wheel still reaches the row underneath.
 *
 * It is a chevron and nothing else. The fade is already saying there is more
 * this way, and a disc of glass laid on top to say it again competes with the
 * artwork it is sitting next to.
 *
 * `arm` is where those buttons sit, measured down from the top: the middle of
 * the artwork rather than the middle of the card, which includes its caption
 * and would put them low. A length rather than a number, because the cards it
 * has to line up with are sized off the window.
 *
 * `name` is which row this is. A scrolled row that is not named is remembered
 * by where it sits in the page, so the row in the same place on the next
 * screen is taken for the same row and opens half way along. Naming it after
 * what is on it — the shelf's order, the scenario a similar rail belongs to —
 * means every row is restored to itself and to nothing else.
 */
export function Rail({ children, name, arm }: { children: ReactNode; name: string; arm: string }) {
  const track = useRef<HTMLDivElement>(null);
  const [runs, setRuns] = useState({ back: false, on: false });

  useEffect(() => {
    const element = track.current;
    if (!element) return;

    const measure = () => {
      const furthest = element.scrollWidth - element.clientWidth;
      setRuns({ back: element.scrollLeft > 4, on: element.scrollLeft < furthest - 4 });
    };

    measure();
    element.addEventListener("scroll", measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => {
      element.removeEventListener("scroll", measure);
      observer.disconnect();
    };
  }, []);

  const step = (direction: number) =>
    track.current?.scrollBy({
      left: direction * track.current.clientWidth * 0.8,
      behavior: "smooth",
    });

  const mask = `linear-gradient(to right, ${
    runs.back ? `transparent 0, black ${RAIL_FADE}` : "black 0"
  }, ${runs.on ? `black calc(100% - ${RAIL_FADE}), transparent 100%` : "black 100%"})`;

  const arrow = `pointer-events-auto flex size-9 items-center justify-center rounded-full text-foreground/50 hover:text-foreground ${panelMotion} ${focusHaloFlush}`;

  return (
    <div className="group/rail relative">
      <div
        ref={track}
        data-scroll-restoration-id={name}
        className="flex gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ maskImage: mask, WebkitMaskImage: mask }}
      >
        {children}
      </div>

      {(
        [
          ["back", -1, "left-0"],
          ["on", 1, "right-0"],
        ] as const
      ).map(([edge, direction, side]) => (
        <div
          key={edge}
          className={`pointer-events-none absolute ${side} flex -translate-y-1/2 opacity-0 transition-opacity duration-200 group-hover/rail:opacity-100 ${
            runs[edge] ? "" : "hidden"
          }`}
          style={{ top: arm }}
        >
          <button
            type="button"
            onClick={() => step(direction)}
            aria-label={direction < 0 ? "Scroll back" : "Scroll on"}
            className={arrow}
          >
            {direction < 0 ? (
              <ChevronLeft className="size-5" strokeWidth={1.75} />
            ) : (
              <ChevronRight className="size-5" strokeWidth={1.75} />
            )}
          </button>
        </div>
      ))}
    </div>
  );
}
